import React, { useMemo, useCallback, useEffect } from "react";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import type { ModalActionsProps } from "~/components/modal.tsx";
import Modal from "~/components/modal.tsx";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import { closeComplete } from "~/features/nav/store-slice.ts";
import { selectRef } from "~/features/nav/selectors.ts";
import { selectGiftRedemption } from "~/features/gift/selectors.ts";
import { pushGtmData } from "~/features/analytics/index.ts";
import { selectOptionalOpenProject } from "~/features/workspace/selectors.ts";
import { isOnceOffLoaded } from "~/utils/loading.ts";
import { usePreventUnloadWarningCallback } from "~/context/before-unload.tsx";
import { useWebsiteUrl } from "~/context/website-url.tsx";
import { buildShopifyGiftRedemptionPath } from "~/features/gift/utils/gift-redemption-utils.ts";
import { completeProject } from "./store-slice.ts";

type CompleteModalProps = {
	readonly show: boolean;
};

function CompleteModal({ show }: CompleteModalProps) {
	const dispatch = useAppDispatch();
	const t = useTranslator();
	const status = useAppSelector((s) => s.order.completion);
	const giftRedemption = useAppSelector(selectGiftRedemption);
	const onCancel = useCallback(() => {
		dispatch(closeComplete());
	}, [dispatch]);

	const openProject = useAppSelector(selectOptionalOpenProject);
	const ref = useAppSelector(selectRef);
	const missingResources = openProject?.build.operationsMissingSources ?? [];
	const hasMissingResources = missingResources.length > 0;
	useEffect(() => {
		if (!show) {
			return;
		}

		if (hasMissingResources) {
			return;
		}

		dispatch(
			pushGtmData({
				event: "Finish",
			}),
		);
		const action = dispatch(completeProject());
		return () => {
			action.abort();
		};
	}, [show, hasMissingResources, dispatch]);

	const addPath = (() => {
		if (!isOnceOffLoaded(status)) {
			return "";
		}

		// Shopify website format
		const params = new URLSearchParams();
		status.data.forEach((item) => {
			params.append(
				"addProductItem",
				JSON.stringify({
					id: item.variantId,
					quantity: item.quantity,
					properties: item.properties,
				}),
			);
		});
		if (ref) {
			params.set("ref", ref);
		}
		return `/pages/add-to-cart/?${params.toString()}`;
	})();
	const addUrl = useWebsiteUrl(addPath);

	const preventUnloadWarning = usePreventUnloadWarningCallback();
	// Build gift redemption URL from single source of truth
	const giftRedemptionPath =
		giftRedemption && status.type === "loaded"
			? buildShopifyGiftRedemptionPath(giftRedemption, status.data)
			: "";
	const giftRedemptionUrl = useWebsiteUrl(giftRedemptionPath);

	const actions = useMemo((): ModalActionsProps["actions"] => {
		const cancel = {
			text: t("Cancel"),
			onClick: onCancel,
			type: "ghost" as const,
		};
		if (status.type === "error" || hasMissingResources) {
			return [cancel];
		}

		// For gift redemption, use Gift Redemption link when URL is present
		if (giftRedemptionPath) {
			return [
				{
					text: "Complete Gift Redemption",
					href: giftRedemptionUrl,
					rel: "noreferrer",
					onMouseDown: preventUnloadWarning,
				},
				cancel,
			];
		}

		// Normal flow - add to cart
		return [
			status.type === "loading"
				? {
						text: t("Building design..."),
						disabled: true,
					}
				: {
						text: t("Add to cart"),
						href: addUrl,
						onMouseDown: preventUnloadWarning,
					},
			cancel,
		];
	}, [
		onCancel,
		hasMissingResources,
		status,
		addUrl,
		preventUnloadWarning,
		t,
		giftRedemptionPath,
		giftRedemptionUrl,
	]);

	return (
		<Modal title={t("Creating product")} show={show} onClose={onCancel}>
			<Modal.Text>
				{t(
					"We're creating the blueprints for your design. Once we're done you can add it to your cart",
				)}
			</Modal.Text>
			{status.type === "error" && (
				<Modal.Text className="form-error">{status.message}</Modal.Text>
			)}
			{missingResources.length > 0 && (
				<Modal.Text className="form-error">
					{t(
						"Some AI filters are still running ({filters}). Once these are completed we can add to cart. (Or you can turn them off if you no longer want them)",
						{ filters: missingResources.join(", ") },
					)}
				</Modal.Text>
			)}
			<Modal.Actions actions={actions} />
		</Modal>
	);
}

export default CompleteModal;
